import { useEffect } from 'react';
import { ConfigProvider, App as AntApp } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { slTheme } from '@/theme';
import { isApiError } from '@starling/core/api/errors';
import type { TenantSettings } from '@/settings/hooks';
import { setBlockedToastHandler, triggerBlockedToast } from '@/lib/blockedToast';
import { LoginPage } from '@/auth/LoginPage';
import { RegisterPage } from '@/auth/RegisterPage';
import { AcceptInvitePage } from '@/auth/AcceptInvitePage';
import { ContextPickerPage } from '@/auth/ContextPickerPage';
import { ForgotPasswordPage, PasswordResetPage } from '@/auth/PasswordResetPage';
import { AppLayout } from '@/components/AppLayout';
import {
  ProtectedRoute,
  RequireListingsFeature,
  RequirePlatformAdmin,
  RequireTenantUser,
} from '@/components/ProtectedRoute';
import { PlatformLayout } from '@/platform/PlatformLayout';
import { SmsCenterPage } from '@/platform/SmsCenterPage';
import { OpticsPage } from '@/platform/OpticsPage';
import { PlatformJournalPage } from '@/platform/PlatformJournalPage';
import { PlatformAdminsPage } from '@/platform/PlatformAdminsPage';
import { PlatformSettingsPage } from '@/platform/PlatformSettingsPage';
import { ModerationPage } from '@/platform/ModerationPage';
import { PricingPage } from '@/platform/PricingPage';
import { CustomersPage } from '@/customers/CustomersPage';
import { CustomerDetailPage } from '@/customers/CustomerDetailPage';
import { OrdersPage } from '@/orders/OrdersPage';
import { OrderDetailPage } from '@/orders/OrderDetailPage';
import { MessagesPage } from '@/messages/MessagesPage';
import { ListingsPage } from '@/listings/ListingsPage';
import { SettingsPage } from '@/settings/SettingsPage';
import { DashboardPage } from '@/components/DashboardPage';
import { NotFoundPage } from '@/components/NotFoundPage';

// M5.7 — сеть безопасности на 403 при блокировке оптики. Глобальный
// MutationCache.onError ловит ЛЮБУЮ мутацию, отбитую бэком с 403, и, если
// settings-кэш подтверждает блокировку (is_active === false), показывает тост.
// Гейт на is_active отсекает role-403 (employee → admin-only) от blocked-403.
// Ссылка на queryClient — через ref, т.к. cache создаётся до самого клиента.
let queryClientRef: QueryClient | null = null;

const mutationCache = new MutationCache({
  onError: (error) => {
    if (!isApiError(error) || error.status !== 403) return;
    const settings = queryClientRef?.getQueryData<TenantSettings>(['tenant', 'settings']);
    if (settings?.is_active === false) {
      triggerBlockedToast();
    }
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Не ретраим 401/403/404/422 — это бизнес-ошибки.
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});
queryClientRef = queryClient;

// Мост: регистрирует тост-функцию (нужен AntD-контекст App.useApp) для
// сети безопасности 403. Ключ 'tenant-blocked' дедуплицирует всплывашки —
// один тост на действие, без спама.
function BlockedToastBridge(): null {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  useEffect(() => {
    setBlockedToastHandler(() => {
      void message.open({ key: 'tenant-blocked', type: 'warning', content: t('blocked.toast') });
    });
    return () => setBlockedToastHandler(null);
  }, [message, t]);
  return null;
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={slTheme} locale={ruRU}>
        <AntApp>
          <BlockedToastBridge />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/select-context" element={<ContextPickerPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/password-reset" element={<PasswordResetPage />} />
              <Route element={<ProtectedRoute />}>
                {/* Смена оптики мид-сессионно (authed): переиспользует пикер в
                    switch-режиме. Public login-режим остаётся на /select-context. */}
                <Route path="/switch-context" element={<ContextPickerPage mode="switch" />} />
                {/* Тенантная админка — admin/employee. platform_admin редиректится
                    в /platform (RequireTenantUser). */}
                <Route element={<RequireTenantUser />}>
                  <Route element={<AppLayout />}>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/customers/:id" element={<CustomerDetailPage />} />
                    <Route path="/orders" element={<OrdersPage />} />
                    <Route path="/orders/:id" element={<OrderDetailPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route element={<RequireListingsFeature />}>
                      <Route path="/listings" element={<ListingsPage />} />
                    </Route>
                    <Route path="/settings" element={<SettingsPage />} />
                  </Route>
                </Route>
                {/* Раздел «Владелец платформы» — только platform_admin. */}
                <Route element={<RequirePlatformAdmin />}>
                  <Route element={<PlatformLayout />}>
                    <Route path="/platform" element={<SmsCenterPage />} />
                    <Route path="/platform/optics" element={<OpticsPage />} />
                    <Route path="/platform/moderation" element={<ModerationPage />} />
                    <Route path="/platform/sms-journal" element={<PlatformJournalPage />} />
                    <Route path="/platform/pricing" element={<PricingPage />} />
                    <Route path="/platform/admins" element={<PlatformAdminsPage />} />
                    <Route path="/platform/settings" element={<PlatformSettingsPage />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
